import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Grid,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import useCompanyId from '../components/e2i/useCompanyId';
import InquiryTable, { InquiryColumn } from '../components/inquiry/InquiryTable';
import SapPODialog from '../components/inquiry/SapPODialog';
import {
  DelayedTextField,
  DocLinks,
  MultiPicker,
  codeOptions,
  codesText,
  hasModule,
  joinList,
  money,
  splitList,
  statusColor,
  useUrlFilters,
} from '../components/inquiry/inquiryCommon';
import inquiryApi from '../services/inquiryApi';
import { showSnackbar } from '../store/slices/uiSlice';
import type { RootState } from '../store';
import type { PrInquiryResult, PrInquiryRow, PrLookups } from '../types/inquiry';
import { saveBlob } from '../utils/formattedSoa';
import { blobErrorText, cleanDateInput, errorText, formatDate, todayIso } from '../utils/e2i';

/**
 * PR Inquiry: search the purchase requisitions of the selected company that the user can read (their own, those
 * they approve or approved, and those of the projects, divisions and departments granted to them in User
 * Management › Access; every one for Admin, Viewer and Procurement Officer). Read-only; a PR opens on its page, its
 * POs on theirs (a SAP PO in the SAP PO view), and the filters stay in the address, so Back returns to the search.
 */
const KEYS = ['q', 'prNumber', 'sapNumber', 'poNumber', 'status', 'type', 'allocation', 'project', 'division', 'department',
  'requester', 'dateFrom', 'dateTo', 'requiredFrom', 'requiredTo', 'amountMin', 'amountMax', 'item', 'vendor', 'sap',
  'sort', 'dir', 'page', 'pageSize'] as const;
const MORE = ['sapNumber', 'poNumber', 'allocation', 'project', 'division', 'department', 'requester', 'dateFrom', 'dateTo',
  'requiredFrom', 'requiredTo', 'amountMin', 'amountMax', 'item', 'vendor', 'sap'];

const PRInquiry: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((s: RootState) => s.auth.user);
  const { companyId, companyName } = useCompanyId();
  const { filters, update, clear } = useUrlFilters(KEYS, 'prpo.prInquiry.search');
  const [lookups, setLookups] = useState<PrLookups | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [result, setResult] = useState<PrInquiryResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [showMore, setShowMore] = useState(() => MORE.some((k) => !!new URLSearchParams(window.location.search).get(k)));
  const [sapPo, setSapPo] = useState<number | null>(null);
  const ticket = useRef(0);
  const canPoInquiry = hasModule(user, 'po-inquiry');

  useEffect(() => {
    if (!companyId) return undefined;
    let cancelled = false;
    setLookupError(null);
    inquiryApi.prLookups(companyId)
      .then((l) => { if (!cancelled) setLookups(l); })
      .catch((e: any) => { if (!cancelled) setLookupError(errorText(e, 'The PR Inquiry could not be opened for this company')); });
    return () => { cancelled = true; };
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    const mine = ++ticket.current;
    setLoading(true);
    setError(null);
    inquiryApi.prSearch(companyId, filters)
      .then((r) => { if (mine === ticket.current) setResult(r); })
      .catch((e: any) => { if (mine === ticket.current) { setResult(null); setError(errorText(e, 'The search failed')); } })
      .finally(() => { if (mine === ticket.current) setLoading(false); });
  }, [companyId, filters]);

  const set = useCallback((key: string) => (value: string) => update({ [key]: value }), [update]);
  const setList = (key: string) => (values: Array<string | number>) => update({ [key]: joinList(values) });
  const setDate = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => update({ [key]: cleanDateInput(e.target.value) });

  const currency = result?.currency || lookups?.company.currency || '';
  const decimals = result?.amountDecimals ?? lookups?.company.amountDecimals ?? 2;
  const amount = (n: number) => money(n, currency, currency, decimals);
  const names = result?.names || { projects: {}, divisions: {}, departments: {} };
  const active = KEYS.filter((k) => !['sort', 'dir', 'page', 'pageSize'].includes(k)).some((k) => !!filters[k]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const blob = await inquiryApi.prExport(companyId, filters);
      saveBlob(blob, `PR-Inquiry-${todayIso()}.xlsx`);
    } catch (e: any) {
      dispatch(showSnackbar({ message: await blobErrorText(e, 'The Excel export failed'), severity: 'error' }));
    } finally {
      setExporting(false);
    }
  };

  const columns: InquiryColumn<PrInquiryRow>[] = useMemo(() => [
    {
      id: 'number', header: 'PR No.', sortKey: 'number', minWidth: 150,
      cell: (r) => (
        <Box>
          <Typography variant="body2" fontWeight={600} color="primary.main">{r.prNumber}</Typography>
          <Typography variant="caption" color="text.secondary">{r.lineCount} line{r.lineCount === 1 ? '' : 's'}</Typography>
        </Box>
      ),
    },
    { id: 'date', header: 'PR date', sortKey: 'date', cell: (r) => formatDate(r.prDate) },
    { id: 'requester', header: 'Requested by', sortKey: 'requester', minWidth: 140, cell: (r) => r.requester.name },
    { id: 'type', header: 'Type', sortKey: 'type', cell: (r) => r.type },
    {
      id: 'allocation', header: 'Project / department', sortKey: 'allocation', minWidth: 220,
      cell: (r) => (
        <Box>
          <Typography variant="body2">{r.allocationCode}{r.allocationName ? ` ${r.allocationName}` : ''}</Typography>
          <Typography variant="caption" color="text.secondary">{r.allocationType === 'CostCenter' ? 'Cost center' : r.allocationType}</Typography>
        </Box>
      ),
    },
    { id: 'divisions', header: 'Divisions', minWidth: 140, cell: (r) => codesText(r.divisions, 'divisions', names) },
    {
      id: 'status', header: 'Status', sortKey: 'status', minWidth: 150,
      cell: (r) => (
        <Box>
          <Chip size="small" color={statusColor(r.status)} label={r.statusLabel || r.status} />
          {r.currentApprover && r.status !== 'Draft' && !r.status.startsWith('Posted') && r.status !== 'Rejected' && (
            <Typography variant="caption" color="text.secondary" display="block">With {r.currentApprover}</Typography>
          )}
          {r.sapPostingStatus === 'Failed' && <Typography variant="caption" color="error" display="block">SAP posting failed</Typography>}
        </Box>
      ),
    },
    { id: 'required', header: 'Required by', sortKey: 'required', cell: (r) => formatDate(r.requiredDate) },
    { id: 'amount', header: 'Total', sortKey: 'amount', align: 'right', minWidth: 120, cell: (r) => <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{amount(r.amount)}</Box> },
    { id: 'sapPr', header: 'SAP PR', cell: (r) => r.sapPrNumbers.join(', ') },
    {
      id: 'pos', header: 'Purchase orders', minWidth: 160,
      cell: (r) => (
        <Box>
          <DocLinks
            items={r.pos}
            label={(p) => (p.source === 'SAP' ? `SAP ${p.number}` : p.number)}
            ariaLabel={(p) => `Open purchase order ${p.number}`}
            onOpen={(p) => {
              if (p.source === 'PRPO' && p.id) navigate(`/po/${p.id}`);
              else if (p.docEntry) setSapPo(p.docEntry);
            }}
          />
          {canPoInquiry && r.pos.length > 0 && (
            <Link
              component="button"
              type="button"
              variant="caption"
              onClick={(e: React.MouseEvent) => { e.stopPropagation(); navigate(`/po-inquiry?prNumber=${encodeURIComponent(r.prNumber)}`); }}
            >
              In PO Inquiry
            </Link>
          )}
        </Box>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [names, currency, decimals, canPoInquiry, navigate]);

  if (!companyId) return <Alert severity="info" sx={{ m: 2 }}>Select a company first.</Alert>;

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <Box>
        <Typography variant="h5" component="h1">PR Inquiry</Typography>
        <Typography variant="body2" color="text.secondary">
          {user?.role === 'Admin' || user?.role === 'Viewer' || user?.role === 'ProcurementOfficer'
            ? `Every purchase requisition of ${lookups?.company.name || companyName || 'this company'}.`
            : `Purchase requisitions of ${lookups?.company.name || companyName || 'this company'}: yours, those you approve, and those of the projects, divisions and departments granted to you.`}
        </Typography>
      </Box>
      {lookupError && <Alert severity="error">{lookupError}</Alert>}

      <Paper variant="outlined" sx={{ p: 2 }} component="section" aria-label="Search filters">
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <DelayedTextField id="pri-q" label="Search" fullWidth size="small" value={filters.q || ''} onValue={set('q')}
              placeholder="PR number, item, requester, project, remarks…" InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} md={2}>
            <DelayedTextField id="pri-number" label="PR No." fullWidth size="small" value={filters.prNumber || ''} onValue={set('prNumber')} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} md={3}>
            <MultiPicker id="pri-status" label="Status" value={splitList(filters.status)} onChange={setList('status')}
              options={(lookups?.statuses || []).map((s) => ({ value: s.value, label: s.label, hint: `${s.count} PR${s.count === 1 ? '' : 's'}` }))} />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField id="pri-type" select label="Type" fullWidth size="small" value={filters.type || ''} onChange={(e) => update({ type: e.target.value })}
              InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }}>
              <MenuItem value="">All types</MenuItem>
              {(lookups?.types || []).map((t) => <MenuItem key={t} value={t}>{t === 'FixedAsset' ? 'Fixed asset' : t}</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>
        <Collapse in={showMore} unmountOnExit>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6} md={4}>
              <MultiPicker id="pri-project" label="Projects" value={splitList(filters.project)} onChange={setList('project')} options={codeOptions(lookups?.codes.projects || [])} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <MultiPicker id="pri-division" label="Divisions" value={splitList(filters.division)} onChange={setList('division')} options={codeOptions(lookups?.codes.divisions || [])} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <MultiPicker id="pri-department" label="Departments" value={splitList(filters.department)} onChange={setList('department')} options={codeOptions(lookups?.codes.departments || [])} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <MultiPicker id="pri-requester" label="Requested by" value={splitList(filters.requester)} onChange={setList('requester')}
                options={(lookups?.requesters || []).map((r) => ({ value: String(r.id), label: r.name || `User ${r.id}` }))} />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField id="pri-allocation" select label="Allocation" fullWidth size="small" value={filters.allocation || ''} onChange={(e) => update({ allocation: e.target.value })}
                InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Project">Project</MenuItem>
                <MenuItem value="CostCenter">Cost center</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField id="pri-sap" select label="In SAP" fullWidth size="small" value={filters.sap || ''} onChange={(e) => update({ sap: e.target.value })}
                InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="posted">Posted to SAP</MenuItem>
                <MenuItem value="notPosted">Not posted</MenuItem>
                <MenuItem value="failed">Posting failed</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField id="pri-date-from" type="date" label="PR date from" fullWidth size="small" value={filters.dateFrom || ''} onChange={setDate('dateFrom')} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField id="pri-date-to" type="date" label="PR date to" fullWidth size="small" value={filters.dateTo || ''} onChange={setDate('dateTo')} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField id="pri-required-from" type="date" label="Required from" fullWidth size="small" value={filters.requiredFrom || ''} onChange={setDate('requiredFrom')} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField id="pri-required-to" type="date" label="Required to" fullWidth size="small" value={filters.requiredTo || ''} onChange={setDate('requiredTo')} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <DelayedTextField id="pri-amount-min" label="Total from" fullWidth size="small" value={filters.amountMin || ''} onValue={set('amountMin')} inputProps={{ inputMode: 'decimal' }} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <DelayedTextField id="pri-amount-max" label="Total to" fullWidth size="small" value={filters.amountMax || ''} onValue={set('amountMax')} inputProps={{ inputMode: 'decimal' }} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <DelayedTextField id="pri-item" label="Item (code or description)" fullWidth size="small" value={filters.item || ''} onValue={set('item')} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <DelayedTextField id="pri-vendor" label="Vendor on the lines" fullWidth size="small" value={filters.vendor || ''} onValue={set('vendor')} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <DelayedTextField id="pri-sap-number" label="SAP PR No." fullWidth size="small" value={filters.sapNumber || ''} onValue={set('sapNumber')} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <DelayedTextField id="pri-po-number" label="PO No." fullWidth size="small" value={filters.poNumber || ''} onValue={set('poNumber')} InputLabelProps={{ shrink: true }} />
            </Grid>
          </Grid>
        </Collapse>
        <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}>
          <Button size="small" startIcon={<FilterListIcon />} onClick={() => setShowMore(!showMore)} aria-expanded={showMore}>
            {showMore ? 'Fewer filters' : 'More filters'}
          </Button>
          <Button size="small" startIcon={<ClearIcon />} onClick={clear} disabled={!active}>Clear filters</Button>
          <Box sx={{ flex: 1 }} />
          <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportExcel} disabled={exporting || !result?.total}>
            {exporting ? 'Exporting…' : 'Export to Excel'}
          </Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error">{error}</Alert>}
      <Typography variant="body2" color="text.secondary" aria-live="polite">
        {result ? `${result.total.toLocaleString()} purchase requisition${result.total === 1 ? '' : 's'} · total ${amount(result.totalAmount)}` : loading ? 'Searching…' : ''}
      </Typography>

      <InquiryTable
        ariaLabel="Purchase requisitions"
        columns={columns}
        rows={result?.rows || []}
        getRowKey={(r) => r.prId}
        rowLabel={(r) => `Open purchase requisition ${r.prNumber}`}
        onRowClick={(r) => navigate(`/pr/${r.prId}`)}
        sort={filters.sort || 'date'}
        dir={filters.dir === 'asc' ? 'asc' : 'desc'}
        onSort={(sort, dir) => update({ sort, dir })}
        page={Number(filters.page) || 1}
        pageSize={Number(filters.pageSize) || 50}
        total={result?.total || 0}
        onPage={(page) => update({ page: String(page) })}
        onPageSize={(size) => update({ pageSize: String(size) })}
        loading={loading}
        emptyMessage={active ? 'No purchase requisition matches these filters.' : 'No purchase requisition to show.'}
      />

      <SapPODialog
        open={sapPo !== null}
        companyId={companyId}
        companyCurrency={currency}
        docEntry={sapPo}
        onClose={() => setSapPo(null)}
        onOpenPr={(pr) => { setSapPo(null); if (pr.id) navigate(`/pr/${pr.id}`); }}
        onOpenPrpoPo={(id) => { setSapPo(null); navigate(`/po/${id}`); }}
      />
    </Box>
  );
};

export default PRInquiry;
